// components/CustomPackageRequests.js - Updated Version
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const CustomPackageRequests = ({ setMessage }) => {
    const { apiRequest } = useAdmin();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');

    // Load custom package requests
    const loadRequests = async () => {
        try {
            setLoading(true);
            const data = await apiRequest('/custom-packages/requests');

            if (data.success) {
                setRequests(data.requests || []);
                setMessage(`✅ Loaded ${data.requests?.length || 0} custom package requests`);
            } else {
                throw new Error(data.error || 'Failed to load requests');
            }
        } catch (error) {
            console.error('Error loading custom package requests:', error);
            setMessage(`❌ Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRequests();
    }, []);

    // Filter requests by status
    const filteredRequests = requests.filter(request => {
        if (statusFilter === 'all') return true;
        return request.status === statusFilter;
    });

    // Update request status
    const updateRequestStatus = async (requestId, status, finalPrice = null, adminNotes = '') => {
        try {
            const updateData = { status };
            if (finalPrice !== null) updateData.finalPrice = finalPrice;
            if (adminNotes) updateData.adminNotes = adminNotes;

            const data = await apiRequest(`/custom-packages/requests/${requestId}`, {
                method: 'PUT',
                body: updateData
            });

            if (data.success) {
                setMessage(`✅ Request ${status} successfully`);
                await loadRequests(); // Reload requests
                setShowDetails(false);
            } else {
                throw new Error(data.error || 'Failed to update request');
            }
        } catch (error) {
            console.error('Error updating request:', error);
            setMessage(`❌ Error: ${error.message}`);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `৳${amount?.toLocaleString() || '0'}`;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Get status badge color
    const getStatusBadge = (status) => {
        const statusColors = {
            pending: 'warning',
            reviewed: 'info',
            quoted: 'primary',
            confirmed: 'success',
            rejected: 'danger'
        };
        return `badge bg-${statusColors[status] || 'secondary'}`;
    };

    // Get travel dates
    const getTravelDates = (request) => {
        return request?.travelDetails?.travelDates || { startDate: null, endDate: null };
    };

    // Get total travelers (adults + children + infants)
    const getTotalTravelers = (request) => {
        const familyMembers = request?.packagePreferences?.familyMembers || { adults: 0, children: 0, infants: 0 };
        return familyMembers.adults + familyMembers.children + familyMembers.infants;
    };

    // Get selected spots count and names
    const getSelectedSpotsInfo = (request) => {
        const selectedSpots = request?.travelDetails?.selectedSpots || [];
        const spotNames = selectedSpots.map(spot => spot.spotName).join(', ');
        return {
            count: selectedSpots.length,
            names: spotNames
        };
    };

    // Get spot names with fares
    const getSpotsWithFares = (request) => {
        const selectedSpots = request?.travelDetails?.selectedSpots || [];
        return selectedSpots.map(spot => ({
            name: spot.spotName,
            fare: spot.fare
        }));
    };

    // Get accommodation type label
    const getAccommodationLabel = (type) => {
        const labels = {
            standard: 'Standard Hotel',
            deluxe: 'Deluxe Hotel',
            luxury: 'Luxury Hotel',
            resort: 'Beach Resort'
        };
        return labels[type] || type;
    };

    // Get transportation label
    const getTransportationLabel = (type) => {
        const labels = {
            bus: 'AC Bus',
            train: 'Train',
            flight: 'Flight',
            private_car: 'Private Car'
        };
        return labels[type] || type;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading custom package requests...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                    <i className="fas fa-clipboard-list me-2 text-primary"></i>
                    Custom Package Requests
                </h4>
                <div className="d-flex align-items-center">
                    {/* Status Filter */}
                    <select
                        className="form-select form-select-sm me-2"
                        style={{ width: '150px' }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="quoted">Quoted</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="rejected">Rejected</option>
                    </select>

                    <button
                        className="btn btn-primary btn-sm"
                        onClick={loadRequests}
                        disabled={loading}
                    >
                        <i className="fas fa-refresh me-1"></i>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="row mb-4">
                <div className="col-md-2 col-6">
                    <div className="card bg-primary text-white">
                        <div className="card-body text-center p-3">
                            <h5 className="card-title">{requests.length}</h5>
                            <p className="card-text small mb-0">Total</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <div className="card bg-warning">
                        <div className="card-body text-center p-3">
                            <h5 className="card-title">{requests.filter(r => r.status === 'pending').length}</h5>
                            <p className="card-text small mb-0">Pending</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <div className="card bg-success text-white">
                        <div className="card-body text-center p-3">
                            <h5 className="card-title">{requests.filter(r => r.status === 'confirmed').length}</h5>
                            <p className="card-text small mb-0">Confirmed</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <div className="card bg-danger text-white">
                        <div className="card-body text-center p-3">
                            <h5 className="card-title">{requests.filter(r => r.status === 'rejected').length}</h5>
                            <p className="card-text small mb-0">Rejected</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <div className="card bg-info text-white">
                        <div className="card-body text-center p-3">
                            <h5 className="card-title">{requests.filter(r => r.status === 'reviewed').length}</h5>
                            <p className="card-text small mb-0">Reviewed</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <div className="card bg-secondary text-white">
                        <div className="card-body text-center p-3">
                            <h5 className="card-title">{requests.filter(r => r.status === 'quoted').length}</h5>
                            <p className="card-text small mb-0">Quoted</p>
                        </div>
                    </div>
                </div>
            </div>

            {filteredRequests.length === 0 ? (
                <div className="text-center py-5">
                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Custom Package Requests</h5>
                    <p className="text-muted">No custom package requests found {statusFilter !== 'all' ? `with status: ${statusFilter}` : ''}.</p>
                </div>
            ) : (
                <div className="row">
                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Requests List ({filteredRequests.length})</h6>
                                <span className="badge bg-secondary">
                                    Showing {filteredRequests.length} of {requests.length}
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <div className="list-group list-group-flush">
                                    {filteredRequests.map((request) => {
                                        const travelDates = getTravelDates(request);
                                        const totalTravelers = getTotalTravelers(request);
                                        const spotsInfo = getSelectedSpotsInfo(request);
                                        const createdAt = new Date(request.createdAt);
                                        const isRecent = (Date.now() - createdAt) < 24 * 60 * 60 * 1000; // Within 24 hours

                                        return (
                                            <div
                                                key={request._id}
                                                className={`list-group-item list-group-item-action cursor-pointer ${selectedRequest?._id === request._id ? 'active' : ''
                                                    } ${isRecent ? 'border-start border-primary border-3' : ''}`}
                                                onClick={() => {
                                                    setSelectedRequest(request);
                                                    setShowDetails(true);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center mb-1">
                                                            <h6 className="mb-0">
                                                                {request.personalInfo?.fullName}
                                                            </h6>
                                                            {isRecent && (
                                                                <span className="badge bg-primary ms-2 small">NEW</span>
                                                            )}
                                                        </div>
                                                        <p className="mb-1 small">
                                                            <i className="fas fa-envelope me-1"></i>
                                                            {request.personalInfo?.email}
                                                        </p>
                                                        <p className="mb-1 small">
                                                            <i className="fas fa-phone me-1"></i>
                                                            {request.personalInfo?.phone}
                                                        </p>
                                                        <p className="mb-1 small">
                                                            <i className="fas fa-map-marker-alt me-1"></i>
                                                            {request.travelDetails?.departureDistrict} → {request.travelDetails?.startBranch?.toUpperCase()}
                                                        </p>
                                                        <p className="mb-1 small">
                                                            <i className="fas fa-users me-1"></i>
                                                            {totalTravelers} Travelers | {spotsInfo.count} Spots
                                                        </p>
                                                        <p className="mb-0 small">
                                                            <i className="fas fa-calendar me-1"></i>
                                                            {formatDate(travelDates.startDate)} - {formatDate(travelDates.endDate)}
                                                        </p>
                                                    </div>
                                                    <div className="text-end">
                                                        <span className={getStatusBadge(request.status)}>
                                                            {request.status?.toUpperCase()}
                                                        </span>
                                                        <div className="mt-2">
                                                            <strong className={request.finalPrice ? 'text-success' : ''}>
                                                                {formatCurrency(request.finalPrice || request.estimatedPrice)}
                                                            </strong>
                                                        </div>
                                                        <div className="small text-muted mt-1">
                                                            {formatDate(request.createdAt)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        {showDetails && selectedRequest ? (
                            <div className="card">
                                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">Request Details</h6>
                                    <div className="d-flex align-items-center">
                                        <span className={getStatusBadge(selectedRequest.status) + ' me-2'}>
                                            {selectedRequest.status?.toUpperCase()}
                                        </span>
                                        <button
                                            className="btn-close"
                                            onClick={() => setShowDetails(false)}
                                        ></button>
                                    </div>
                                </div>
                                <div className="card-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                    {/* Request ID and Date */}
                                    <div className="mb-4">
                                        <div className="row small">
                                            <div className="col-6">
                                                <strong>Request ID:</strong><br />
                                                <code className="text-muted">{selectedRequest._id}</code>
                                            </div>
                                            <div className="col-6">
                                                <strong>Submitted:</strong><br />
                                                {formatDate(selectedRequest.createdAt)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Personal Information */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2">
                                            <i className="fas fa-user me-2"></i>
                                            Personal Information
                                        </h6>
                                        <div className="row small">
                                            <div className="col-6 mb-2">
                                                <strong>Name:</strong><br />
                                                {selectedRequest.personalInfo?.fullName || 'N/A'}
                                            </div>
                                            <div className="col-6 mb-2">
                                                <strong>Email:</strong><br />
                                                {selectedRequest.personalInfo?.email || 'N/A'}
                                            </div>
                                            <div className="col-6 mb-2">
                                                <strong>Phone:</strong><br />
                                                {selectedRequest.personalInfo?.phone || 'N/A'}
                                            </div>
                                            <div className="col-6 mb-2">
                                                <strong>Emergency:</strong><br />
                                                {selectedRequest.personalInfo?.emergencyContact || 'N/A'}
                                            </div>
                                            {selectedRequest.personalInfo?.address && (
                                                <div className="col-12 mb-2">
                                                    <strong>Address:</strong><br />
                                                    {selectedRequest.personalInfo.address}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Travel Details */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2">
                                            <i className="fas fa-map-marked-alt me-2"></i>
                                            Travel Details
                                        </h6>
                                        <div className="row small">
                                            <div className="col-6 mb-2">
                                                <strong>Departure:</strong><br />
                                                {selectedRequest.travelDetails?.departureDistrict || 'N/A'}
                                            </div>
                                            <div className="col-6 mb-2">
                                                <strong>Start Branch:</strong><br />
                                                <span className="badge bg-primary">
                                                    {selectedRequest.travelDetails?.startBranch?.toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="col-12 mb-2">
                                                <strong>Travel Dates:</strong><br />
                                                {formatDate(selectedRequest.travelDetails?.travelDates?.startDate)} - {formatDate(selectedRequest.travelDetails?.travelDates?.endDate)}
                                                <span className="badge bg-secondary ms-2">
                                                    {selectedRequest.travelDetails?.numberOfDays} Days
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Selected Spots */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2">
                                            <i className="fas fa-map-marker-alt me-2"></i>
                                            Selected Spots ({getSelectedSpotsInfo(selectedRequest).count})
                                        </h6>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Spot Name</th>
                                                        <th>Fare</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getSpotsWithFares(selectedRequest).map((spot, index) => (
                                                        <tr key={index}>
                                                            <td>{index + 1}</td>
                                                            <td>{spot.name}</td>
                                                            <td className="text-success fw-bold">
                                                                {formatCurrency(spot.fare)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    <tr className="table-light">
                                                        <td colSpan="2" className="text-end fw-bold">Total Spots Fare:</td>
                                                        <td className="text-success fw-bold">
                                                            {formatCurrency(selectedRequest.pricingBreakdown?.spotsTotal || 0)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Package Preferences */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2">
                                            <i className="fas fa-cogs me-2"></i>
                                            Package Preferences
                                        </h6>
                                        <div className="row small">
                                            <div className="col-6 mb-2">
                                                <strong>Accommodation:</strong><br />
                                                {getAccommodationLabel(selectedRequest.packagePreferences?.accommodationType)}
                                            </div>
                                            <div className="col-6 mb-2">
                                                <strong>Transportation:</strong><br />
                                                {getTransportationLabel(selectedRequest.packagePreferences?.transportation)}
                                            </div>
                                            <div className="col-6 mb-2">
                                                <strong>Meal Plan:</strong><br />
                                                {selectedRequest.packagePreferences?.mealPlan || 'N/A'}
                                            </div>
                                            <div className="col-6 mb-2">
                                                <strong>Travelers:</strong><br />
                                                <span className="badge bg-info">
                                                    Adults: {selectedRequest.packagePreferences?.familyMembers?.adults || 0}
                                                </span>
                                                <span className="badge bg-secondary mx-1">
                                                    Children: {selectedRequest.packagePreferences?.familyMembers?.children || 0}
                                                </span>
                                                <span className="badge bg-light text-dark">
                                                    Infants: {selectedRequest.packagePreferences?.familyMembers?.infants || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Requirements */}
                                    {(selectedRequest.additionalRequirements?.specialRequirements ||
                                        selectedRequest.additionalRequirements?.dietaryRestrictions ||
                                        selectedRequest.additionalRequirements?.additionalNotes) && (
                                            <div className="mb-4">
                                                <h6 className="border-bottom pb-2">
                                                    <i className="fas fa-clipboard-list me-2"></i>
                                                    Additional Requirements
                                                </h6>
                                                {selectedRequest.additionalRequirements?.specialRequirements && (
                                                    <div className="mb-2">
                                                        <strong>Special Requirements:</strong><br />
                                                        <div className="alert alert-light p-2 small">
                                                            {selectedRequest.additionalRequirements.specialRequirements}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedRequest.additionalRequirements?.dietaryRestrictions && (
                                                    <div className="mb-2">
                                                        <strong>Dietary Restrictions:</strong><br />
                                                        <span className="badge bg-warning">
                                                            {selectedRequest.additionalRequirements.dietaryRestrictions}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedRequest.additionalRequirements?.additionalNotes && (
                                                    <div>
                                                        <strong>Additional Notes:</strong><br />
                                                        <div className="alert alert-light p-2 small">
                                                            {selectedRequest.additionalRequirements.additionalNotes}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    {/* Pricing Information */}
                                    <div className="mb-4">
                                        <h6 className="border-bottom pb-2">
                                            <i className="fas fa-money-bill-wave me-2"></i>
                                            Pricing Breakdown
                                        </h6>
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td>Spots Total:</td>
                                                        <td className="text-end">{formatCurrency(selectedRequest.pricingBreakdown?.spotsTotal || 0)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Accommodation:</td>
                                                        <td className="text-end">{formatCurrency(selectedRequest.pricingBreakdown?.accommodationCost || 0)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Transport:</td>
                                                        <td className="text-end">{formatCurrency(selectedRequest.pricingBreakdown?.transportCost || 0)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Meals:</td>
                                                        <td className="text-end">{formatCurrency(selectedRequest.pricingBreakdown?.mealCost || 0)}</td>
                                                    </tr>
                                                    <tr className="table-light">
                                                        <td>Subtotal:</td>
                                                        <td className="text-end fw-bold">
                                                            {formatCurrency(selectedRequest.pricingBreakdown?.totalBeforeMultiplier || 0)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Branch Multiplier ({selectedRequest.pricingBreakdown?.branchMultiplier || 1}x):</td>
                                                        <td className="text-end"></td>
                                                    </tr>
                                                    <tr className="table-success">
                                                        <td>
                                                            <strong>Total Estimated Price:</strong>
                                                        </td>
                                                        <td className="text-end">
                                                            <h5 className="text-success mb-0">
                                                                {formatCurrency(selectedRequest.estimatedPrice || 0)}
                                                            </h5>
                                                        </td>
                                                    </tr>
                                                    {selectedRequest.finalPrice && (
                                                        <tr className="table-primary">
                                                            <td>
                                                                <strong>Final Price:</strong>
                                                            </td>
                                                            <td className="text-end">
                                                                <h5 className="text-primary mb-0">
                                                                    {formatCurrency(selectedRequest.finalPrice)}
                                                                </h5>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Admin Notes */}
                                    {selectedRequest.adminNotes && (
                                        <div className="mb-4">
                                            <h6 className="border-bottom pb-2">
                                                <i className="fas fa-sticky-note me-2"></i>
                                                Admin Notes
                                            </h6>
                                            <div className="alert alert-info p-2 small">
                                                {selectedRequest.adminNotes}
                                            </div>
                                        </div>
                                    )}

                                    {/* Admin Actions */}
                                    <div className="border-top pt-3">
                                        <h6 className="mb-3">Admin Actions</h6>
                                        <div className="row g-2 mb-3">
                                            <div className="col-6">
                                                <button
                                                    className="btn btn-success btn-sm w-100"
                                                    onClick={() => updateRequestStatus(selectedRequest._id, 'confirmed')}
                                                    disabled={selectedRequest.status === 'confirmed'}
                                                >
                                                    <i className="fas fa-check me-1"></i>
                                                    Confirm
                                                </button>
                                            </div>
                                            <div className="col-6">
                                                <button
                                                    className="btn btn-danger btn-sm w-100"
                                                    onClick={() => updateRequestStatus(selectedRequest._id, 'rejected')}
                                                    disabled={selectedRequest.status === 'rejected'}
                                                >
                                                    <i className="fas fa-times me-1"></i>
                                                    Reject
                                                </button>
                                            </div>
                                            <div className="col-6">
                                                <button
                                                    className="btn btn-info btn-sm w-100"
                                                    onClick={() => updateRequestStatus(selectedRequest._id, 'reviewed')}
                                                    disabled={selectedRequest.status === 'reviewed'}
                                                >
                                                    <i className="fas fa-eye me-1"></i>
                                                    Review
                                                </button>
                                            </div>
                                            <div className="col-6">
                                                <button
                                                    className="btn btn-primary btn-sm w-100"
                                                    onClick={() => updateRequestStatus(selectedRequest._id, 'quoted')}
                                                    disabled={selectedRequest.status === 'quoted'}
                                                >
                                                    <i className="fas fa-tag me-1"></i>
                                                    Quote
                                                </button>
                                            </div>
                                        </div>

                                        {/* Final Price Input */}
                                        <div className="mb-3">
                                            <label className="form-label small">Set Final Price (Optional)</label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text">৳</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Enter final price"
                                                    defaultValue={selectedRequest.finalPrice || selectedRequest.estimatedPrice}
                                                    onBlur={(e) => {
                                                        const finalPrice = parseFloat(e.target.value);
                                                        if (!isNaN(finalPrice) && finalPrice > 0) {
                                                            updateRequestStatus(selectedRequest._id, selectedRequest.status, finalPrice);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Admin Notes Input */}
                                        <div>
                                            <label className="form-label small">Add Admin Notes</label>
                                            <textarea
                                                className="form-control form-control-sm mb-2"
                                                rows="2"
                                                placeholder="Add notes..."
                                                onBlur={(e) => {
                                                    if (e.target.value.trim()) {
                                                        updateRequestStatus(selectedRequest._id, selectedRequest.status, null, e.target.value);
                                                    }
                                                }}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="card">
                                <div className="card-body text-center text-muted py-5">
                                    <i className="fas fa-info-circle fa-2x mb-3"></i>
                                    <p>Select a request to view details</p>
                                    <small className="text-muted">
                                        Click on any request from the list to view complete details
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomPackageRequests;